import React, { useState, useEffect, useRef } from 'react';
import type { RootState } from '@/rootReducer';
import {
	FiltersWhite,
	SearchIconGold,
	XIconGold,
	DropDown,
	ChevronColorless,
	SearchIcon
} from '@/assets/images';
import DatePicker, {
	staticRangesStandard
} from '@/components/DatePicker/DatePicker';
import type { List } from '@/modules/dashboard/dashboardTypes';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import './FilterComponent.scss';
import { sendAnalytics } from '@/services/analyticsService';
import {
	dashboardGetCommunicationCenterBusinesses,
	DashboardGetCommunicationCenterBusinessesProps
} from '@/modules/dashboard/dashboardActions';
import Button from '@/components/Button/newButtons';
import { emailGetEmailTemplatesRequest } from '../../modules/email/emailActions';
import SortByFilter from '../../modules/communicationCenter/components/CommunicationCenterListItem/SortByFilter';

type FilterComponentProps = {
	pageName: string;
	lists: any;
};

const FilterComponent: React.FC<FilterComponentProps> = ({
	pageName,
	lists
}) => {
	const dispatch = useDispatch();
	const [contactedBusinesses, contactedBusinessesCount, user] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn([
				'businesses',
				'communicationCenter',
				'data'
			]),
			state.dashboard.getIn([
				'businesses',
				'communicationCenter',
				'count'
			]),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const [searchFocused, setSearchFocused] = useState<boolean>(false);
	const [searchIcon, setSearchIcon] = useState(true);
	const [filtersSelectedCount, setFiltersSelectedCount] = useState(0);
	const [filteredLists, setFilteredLists] = useState<List[]>(lists);
	const [selectedListFilter, setSelectedListFilter] =
		useState<string>('All Lists');
	const [listFilterInputValue, setListFilterInputValue] =
		useState<string>('');
	const [contactedBusinessesFiltered, setContactedBusinessesFiltered] =
		useState([]);

	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (contactedBusinesses) {
			const allReplied = contactedBusinesses.filter(
				b => b.emailReplied === true && b.emailRepliedBody
			);
			const notReplied = contactedBusinesses.filter(
				b => b.emailReplied == false || !b.emailRepliedBody
			);
			const allBs = [...allReplied, ...notReplied];

			setContactedBusinessesFiltered(contactedBusinesses);
		}
	}, [JSON.stringify(contactedBusinesses)]);

	const getDateTwoMonthsAgo = () => {
		const dateUnix = new Date().getTime();
		const dateTwoMonths = dateUnix - 60 * 24 * 60 * 60 * 1000;
		return dateTwoMonths;
	};

	const closeListFilter = () => {
		const el = document.getElementById(
			`${pageName}-input-list-element-filter`
		);
		if (el) {
			el.classList.remove('input-open');
		}
	};

	const getBusinesses = (
		q: DashboardGetCommunicationCenterBusinessesProps
	) => {
		return dispatch(dashboardGetCommunicationCenterBusinesses(q));
	};
	useEffect(() => {
		if (lists && lists.length > 0) {
			setFilteredLists(lists);
		}
	}, [lists]);

	const getEmailTemplates = () => dispatch(emailGetEmailTemplatesRequest());

	const queryObject: DashboardGetCommunicationCenterBusinessesProps = {
		contacted: false,
		contactedMessenger: false,
		contactedInstagramMessenger: false,
		contactedPhone: false,
		listId: '',
		repliedYes: false,
		repliedNo: false,
		includes: [],
		excludes: [],
		sort: { lastContact: -1 },
		limit: 150,
		skip: 0,
		returnCount: true,
		openCountZero: false,
		openCountOneFour: false,
		openCountFiveEight: false,
		openCountNinePlus: false,
		leadYes: false,
		leadNo: false,
		leadUnmarked: false,
		startDate: getDateTwoMonthsAgo(),
		endDate: new Date().getTime()
	};

	const sortByOptions = [
		'Sort A-Z by name',
		'Sort Z-A by name',
		'Last contacted date',
		'Last reply date',
		'Last open date'
	];

	const [query, setQuery] =
		useState<DashboardGetCommunicationCenterBusinessesProps>(queryObject);
	useEffect(() => {
		// Handle filter count calculation
		const count = Object.values(query).filter(value => value).length;
		setFiltersSelectedCount(count);
	}, [query]);

	const clearFilterObject: DashboardGetCommunicationCenterBusinessesProps = {
		contacted: false,
		contactedMessenger: false,
		contactedInstagramMessenger: false,
		contactedPhone: false,
		listId: query.listId,
		repliedYes: false,
		repliedNo: false,
		includes: [document.getElementById(`${pageName}-input-element`)?.value],
		excludes: [],
		sort: query.sort,
		limit: 150,
		skip: 0,
		returnCount: true,
		openCountZero: false,
		openCountOneFour: false,
		openCountFiveEight: false,
		openCountNinePlus: false,
		leadYes: false,
		leadNo: false,
		leadUnmarked: false,
		startDate: query.startDate,
		endDate: query.endDate
	};

	const clearFilter = () => {
		setQuery(clearFilterObject);
		getBusinesses(clearFilterObject);
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `Cleared filters ${pageName}`
		});
	};

	const setNewQuery = (key, newValue) => {
		const newQuery = { ...query };
		if (key === 'includes') {
			if (newValue && newValue.length > 0) {
				newQuery[key] = [];
				newQuery[key].unshift(newValue);
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Filtered ${pageName} by Search Words`,
						properties: {
							searchedWords: newQuery[key]
						}
					});
				}
			} else {
				newQuery[key] = [];
			}
			setSearchIcon(false);
		}
		if (key === 'contactMethod') {
			newQuery[`contactedEmail`] = false;
			newQuery[`contactedMessenger`] = false;
			newQuery['contactedInstagramMessenger'] = false;
			newQuery[`contactedPhone`] = false;
			newQuery[`contacted${newValue}`] = true;
			if (
				newQuery[`contacted${newValue}`] ===
				query[`contacted${newValue}`]
			) {
				newQuery[`contacted${newValue}`] = false;
				newQuery[`repliedYes`] = false;
				newQuery[`repliedNo`] = false;
			} else if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `Filtered ${pageName} by Contact Method`,
					properties: {
						selected: newValue
					}
				});
			}
			if (`contacted${newValue}` !== 'contactedEmail') {
				newQuery[`repliedYes`] = false;
				newQuery[`repliedNo`] = false;
				newQuery[`openCountZero`] = false;
				newQuery[`openCountOneFour`] = false;
				newQuery[`openCountFiveEight`] = false;
				newQuery[`openCountNinePlus`] = false;
			}
		}
		if (key === 'repliedYes' || key === 'repliedNo') {
			if (
				query[`contactedMessenger`] === true ||
				query[`contactedPhone`] === true ||
				query['contactedInstagramMessenger'] == true
			) {
				return;
			}
			newQuery[`repliedYes`] = false;
			newQuery[`repliedNo`] = false;
			newQuery[key] = newValue;
			if (newQuery[key] === query[key]) {
				newQuery[key] = !newValue;
			} else {
				newQuery[`contactedEmail`] = true;
				newQuery[`contactedMessenger`] = false;
				newQuery['contactedInstagramMessenger'] = false;
				newQuery[`contactedPhone`] = false;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Filtered ${pageName} by Replied`,
						properties: {
							selected: key
						}
					});
				}
			}
		}
		if (
			key === 'openCountZero' ||
			key === 'openCountOneFour' ||
			key === 'openCountFiveEight' ||
			key === 'openCountNinePlus'
		) {
			if (
				query[`contactedMessenger`] === true ||
				query[`contactedPhone`] === true ||
				query['contactedInstagramMessenger'] == true
			) {
				return;
			}
			newQuery[key] = newValue;
			if (newQuery[key] === query[key]) {
				newQuery[key] = !newValue;
			} else {
				newQuery[`contactedMessenger`] = false;
				newQuery['contactedInstagramMessenger'] = false;
				newQuery[`contactedPhone`] = false;
				newQuery[`contactedEmail`] = true;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Filtered ${pageName} by Open Count`,
						properties: {
							selected: key
						}
					});
				}
			}
		}
		if (key === 'openCountAll') {
			newQuery[`contactedEmail`] = true;
			newQuery[`contactedMessenger`] = false;
			newQuery['contactedInstagramMessenger'] = false;
			newQuery[`contactedPhone`] = false;
			newQuery['openCountOneFour'] = true;
			newQuery['openCountFiveEight'] = true;
			newQuery['openCountNinePlus'] = true;
		}
		if (key === 'leadYes' || key === 'leadNo' || key === 'leadUnmarked') {
			newQuery[`leadYes`] = false;
			newQuery[`leadNo`] = false;
			newQuery[`leadUnmarked`] = false;
			newQuery[key] = newValue;
			if (newQuery[key] === query[key]) {
				newQuery[key] = !newValue;
			} else if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `Filtered ${pageName} by Lead Status`,
					properties: {
						selected: key
					}
				});
			}
		}
		if (key === 'listId') {
			newQuery[key] = newValue;
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `Filtered ${pageName} by List`,
					properties: {
						listId: newValue
					}
				});
			}
			closeListFilter();
		}
		if (key === 'date') {
			const startDateUnix = new Date(newValue.startDate).getTime();
			const endDateUnix = new Date(newValue.endDate).getTime();
			const lessThanDay =
				(endDateUnix - startDateUnix) / 1000 / 60 / 60 / 24 < 1;
			newQuery['startDate'] = startDateUnix;
			newQuery['endDate'] = endDateUnix;
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `Filtered ${pageName} by Date`,
					properties: {
						startDate: newValue.startDate,
						endDate: newValue.endDate
					}
				});
			}
		}
		if (key === 'sortBy') {
			switch (newValue) {
				case 'Sort A-Z by name':
					newQuery['sort'] = { businessName: 1 };
					break;
				case 'Sort Z-A by name':
					newQuery['sort'] = { businessName: -1 };
					break;
				case 'Last contacted date':
					newQuery['sort'] = { lastContact: -1 };
					break;
				case 'Last reply date':
					newQuery['sort'] = { lastReply: -1 };
					break;
				case 'Last open date':
					newQuery['sort'] = { lastOpen: -1 };
					break;
			}
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User sorted ${pageName}`,
				properties: {
					soryBy: newValue
				}
			});
		}
		newQuery['skip'] = 0;
		setQuery(newQuery);
		getBusinesses(newQuery);
	};

	const countFiltersSelected = () => {
		let count = 0;

		Object.entries(query).forEach(query => {
			if (
				(query[0] === 'contactedEmail' ||
					query[0] === 'contactedMessenger' ||
					query[0] === 'contactedInstagramMessenger' ||
					query[0] === 'contactedPhone' ||
					query[0] === 'repliedYes' ||
					query[0] === 'repliedNo' ||
					query[0] === 'openCountZero' ||
					query[0] === 'openCountOneFour' ||
					query[0] === 'openCountFiveEight' ||
					query[0] === 'openCountNinePlus' ||
					query[0] === 'leadYes' ||
					query[0] === 'leadNo' ||
					query[0] === 'leadUnmarked') &&
				query[1]
			) {
				count++;
			}
		});

		setFiltersSelectedCount(count);
	};

	useEffect(() => {
		countFiltersSelected();
	}, [query]);

	const clearQuery = () => {
		setQuery({
			includes: [],
			contactedEmail: false,
			contactedMessenger: false,
			contactedInstagramMessenger: false,
			contactedPhone: false,
			repliedYes: false,
			repliedNo: false,
			openCountZero: false,
			openCountOneFour: false,
			openCountFiveEight: false,
			openCountNinePlus: false,
			leadYes: false,
			leadNo: false,
			leadUnmarked: false,
			listId: null,
			date: {
				startDate: new Date(), // Default start date
				endDate: new Date() // Default end date
			},
			skip: 0,
			limit: 10
		});
	};

	const [disabledSearch, setDisabledSearch] = useState(true);

	const handleSearch = () => {
		if (inputRef.current) setNewQuery('includes', inputRef.current.value);
	};

	const handleKeyUp = (evt: React.KeyboardEvent<HTMLInputElement>) => {
		if (evt.key === 'Enter') {
			const value = evt.currentTarget.value.trim();

			if (query.includes?.includes(value)) return;

			setNewQuery('includes', value);
		}
	};

	const handleClear = () => {
		if (inputRef.current) {
			inputRef.current.value = '';
			clearQuery();
			setNewQuery('includes', '');
			setSearchIcon(true);
			setDisabledSearch(true);
			inputRef.current.focus();
		}
	};

	const searchFilter = (term: string) => {
		const newLists = lists.filter((list: List) =>
			list.name?.toLowerCase().includes(term.toLowerCase())
		);
		setFilteredLists(newLists);
	};

	const handleSortBySelect = (option: string) => {
		setNewQuery('sortBy', option);
	};

	useEffect(() => {
		getBusinesses(query);
		getEmailTemplates();
		if (window.location.search.includes('replies=yes')) {
			handleSortBySelect('Last reply date');
		}
		if (window.location.search.includes('opens=yes')) {
			handleSortBySelect('Last open date');
		}
	}, []);

	const openFilter = (e: HTMLElement | null) => {
		if (!e) return;
		if (e.classList.contains('input-open')) {
			e.classList.remove('input-open');
		} else {
			e.classList.add('input-open');
		}
	};

	const paginate = direction => {
		if (direction === 'forward') {
			query.skip += query.limit;
			getBusinesses(query);
		} else if (direction === 'backward') {
			query.skip -= query.limit;
			getBusinesses(query);
		}
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Paginated ${pageName}`,
				properties: {
					paginationDirection: direction
				}
			});
		}
	};

	return (
		<div className={`${pageName}-header-filter flex flex-col`}>
			<section className="flex w-full flex-col justify-between gap-4 py-4 xl:flex-row xl:items-center">
				<div className={`${pageName}-filter-holder-filter`}>
					{/* Search Input */}
					<label
						className={`${pageName}-input-filter search ${
							searchFocused ? 'focused' : ''
						}`}
						onClick={e => {
							setSearchFocused(false);
							const tar = e.currentTarget;
							setTimeout(() => openFilter(tar), 0);
						}}
						htmlFor={`${pageName}-input-element-filter`}
					>
						<input
							id={`${pageName}-input-elemen-filter`}
							ref={inputRef}
							className={`${pageName}-input-element-filter`}
							placeholder="Search"
							onKeyUp={handleKeyUp}
							onChange={e => setDisabledSearch(!e.target.value)}
						/>
						<button
							type="button"
							onClick={searchIcon ? handleSearch : handleClear}
							disabled={disabledSearch}
						>
							{searchIcon ? (
								<SearchIconGold
									className={`${pageName}-filter-icon-search-filter`}
								/>
							) : (
								<XIconGold
									className={`${pageName}-filter-icon-search-filter`}
								/>
							)}
						</button>
					</label>

					{/* Filters */}
					<div
						id={`${pageName}-filters`}
						className={`${pageName}-input-filter lists ${
							searchFocused ? 'shrinked' : ''
						}`}
						onClick={e => {
							setSearchFocused(false);
							const tar = e.currentTarget;
							setTimeout(() => openFilter(tar), 0);
						}}
					>
						<FiltersWhite className="filter-svg filter" />
						<div
							className={`${pageName}-input-inner-div-filter filters`}
						>
							Filter
							{filtersSelectedCount > 0 && (
								<span
									className={`${pageName}-filter-indicator-filter`}
								>
									{filtersSelectedCount}
								</span>
							)}
							<DropDown
								className={`${pageName}-filter-icon-chevron`}
							/>
						</div>

						{/* Filter Dropdown */}
						<div className="comm-dropdown-bg" />
						<div
							className={`${pageName}-filters-dropdown-filter`}
							onClick={e => e.stopPropagation()}
						>
							<div className={`${pageName}-filters-row-filter`}>
								<div
									className={`${pageName}-filters-clear`}
									onClick={() => {
										clearFilter();
									}}
								>
									Clear Selections
								</div>
								<div
									className={`${pageName}-filters-row-title`}
								>
									Contact Method
								</div>
								<div
									className={` ${pageName}-filters-row-button ${pageName}-filters-row-button-first ${query.contactedEmail ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('contactMethod', 'Email');
									}}
								>
									Email
								</div>
								<div
									className={` ${pageName}-filters-row-button ${query.contactedMessenger ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery(
											'contactMethod',
											'Messenger'
										);
									}}
								>
									Facebook Messenger
								</div>
								<div
									className={` ${pageName}-filters-row-button ${
										query.contactedInstagramMessenger
											? 'row-button-active'
											: ''
									} `}
									onClick={() => {
										setNewQuery(
											'contactMethod',
											'InstagramMessenger'
										);
									}}
								>
									Instagram Messenger
								</div>
								<div
									className={` ${pageName}-filters-row-button ${query.contactedPhone ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('contactMethod', 'Phone');
									}}
								>
									Phone
								</div>
							</div>
							{(user?.threadedEmail ||
								user?.re2HostedEmail ||
								(user.re2SharedDomain &&
									user?.features?.sharedDomainVersion ===
										'v2.1')) && (
								<div
									className={`${pageName}-filters-row-filter`}
									data-disabled={
										query[`contactedMessenger`] ||
										query[`contactedPhone`] ||
										query[`contactedInstagramMessenger`]
									}
								>
									<div
										className={`${pageName}-filters-row-title`}
									>
										Business Replied
									</div>
									<button
										type="button"
										className={`${pageName}-filters-row-button ${pageName}-filters-row-button-first`}
										data-active={query.repliedYes}
										onClick={() => {
											setNewQuery('repliedYes', true);
										}}
									>
										Yes
									</button>

									<button
										type="button"
										className={`${pageName}-filters-row-button`}
										data-active={query.repliedNo}
										onClick={() => {
											setNewQuery('repliedNo', true);
										}}
									>
										No
									</button>
									<div
										className={`${pageName}-filters-row-disabled`}
										data-hidden={
											!(
												query[`contactedMessenger`] ||
												query[`contactedPhone`] ||
												query[
													`contactedInstagramMessenger`
												]
											)
										}
									>
										This filter is only available for the
										Email contact method
									</div>
								</div>
							)}
							<div
								className={` ${pageName}-filters-row-filter ${query[`contactedMessenger`] === true ? 'row-disabled' : ''} ${query[`contactedPhone`] === true ? 'row-disabled' : ''} ${
									query[`contactedInstagramMessenger`] ===
									true
										? 'row-disabled'
										: ''
								} `}
							>
								<div
									className={`${pageName}-filters-row-title`}
								>
									Number of Opens
								</div>
								<div
									className={` ${pageName}-filters-row-button ${pageName}-filters-row-button-first ${query.openCountZero ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('openCountZero', true);
									}}
								>
									Zero
								</div>
								<div
									className={` ${pageName}-filters-row-button ${query.openCountOneFour ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('openCountOneFour', true);
									}}
								>
									1-4
								</div>
								<div
									className={` ${pageName}-filters-row-button ${query.openCountFiveEight ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('openCountFiveEight', true);
									}}
								>
									5-8
								</div>
								<div
									className={` ${pageName}-filters-row-button ${query.openCountNinePlus ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('openCountNinePlus', true);
									}}
								>
									9+
								</div>
								<div
									className={`${pageName}-filters-row-disabled`}
								>
									This filter is only available for the Email
									contact method
								</div>
							</div>
							<div className={`${pageName}-filters-row`}>
								<div
									className={`${pageName}-filters-row-title`}
								>
									Status
								</div>
								<div
									className={` ${pageName}-filters-row-button ${pageName}-filters-row-button-first ${query.leadYes ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('leadYes', true);
									}}
								>
									Lead
								</div>
								<div
									className={` ${pageName}-filters-row-button ${query.leadNo ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('leadNo', true);
									}}
								>
									No Lead
								</div>
								<div
									className={` ${pageName}-filters-row-button ${query.leadUnmarked ? 'row-button-active' : ''} `}
									onClick={() => {
										setNewQuery('leadUnmarked', true);
									}}
								>
									Not Marked
								</div>
							</div>
						</div>
					</div>

					{/* Sort By Filter */}
					<SortByFilter
						searchFocused={searchFocused}
						setSearchFocused={setSearchFocused}
						handleSortBySelect={handleSortBySelect}
						sortByOptions={sortByOptions}
						displayText="Sort"
					/>

					{/* Filter by List */}
					<div
						id="communication-center-input-list-element-filter"
						className={`${pageName}-input-filter lists ${
							searchFocused ? 'shrinked' : ''
						}`}
						onClick={e => {
							setSearchFocused(false);
							const tar = e.currentTarget;
							setTimeout(() => openFilter(tar), 0);
						}}
					>
						<div className={`${pageName}-input-inner-div-filter`}>
							<span
								className={`${pageName}-input-lists-header-filter`}
							>
								{selectedListFilter || 'Filter by List'}
							</span>
							<DropDown
								className={`${pageName}-filter-icon-chevron-filter`}
							/>
						</div>
						<div className={`${pageName}-dropdown-bg-filter`} />
						<div
							className={`${pageName}-list-dropdown-filter`}
							onClick={e => e.stopPropagation()}
						>
							<div className={`${pageName}-search-filter`}>
								<input
									className="search-input-header-filter w-full text-white"
									type="text"
									placeholder="Search"
									value={listFilterInputValue}
									onChange={e => {
										setListFilterInputValue(e.target.value);
										searchFilter(e.target.value);
									}}
								/>
								<div className="d-inline-block search-icon absolute top-1/2 -translate-y-1/2 transform">
									<SearchIcon
										className={`${pageName}-search-icon-filter`}
									/>
								</div>
							</div>
							<div className={`${pageName}-items-filter`}>
								<div
									className={`${pageName}-list-row-filter ${
										selectedListFilter === 'All Lists'
											? 'selected'
											: ''
									}`}
									onClick={() => {
										setSelectedListFilter('All Lists');
										setNewQuery('listId', null);
										closeListFilter();
									}}
								>
									<div className="report-list-row-title">
										All Lists
									</div>
								</div>
								{filteredLists.map((list: List) => (
									<div
										className={`${pageName}-list-row-filter ${
											selectedListFilter === list.name
												? 'selected'
												: ''
										}`}
										key={list.id}
										onClick={() => {
											setSelectedListFilter(list.name);
											setNewQuery('listId', list.id);
											closeListFilter();
										}}
									>
										<div
											className={`${pageName}-list-row-title-filter`}
										>
											{list.name}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>

				{/* Pagination and Date Picker */}
				<div className="flex h-fit items-center gap-3 border-dm-gray xl:border-l-2 xl:pl-3">
					<DatePicker
						wrapperClassName={`${pageName}-date-picker`}
						onChange={evt => setNewQuery('date', evt)}
						maxDate={new Date()}
						minDate={new Date(0)}
						reportUserPeriod={{
							startDate: new Date(getDateTwoMonthsAgo()),
							endDate: new Date().getTime()
						}}
						staticRanges={staticRangesStandard}
					/>
					<div className={`${pageName}-count`}>
						<ChevronColorless
							className={`${pageName}-page-arrow ${pageName}-page-arrow-left ${
								query.skip - query.limit < 0
									? 'disabled-button'
									: ''
							}`}
							onClick={() => {
								if (query.skip - query.limit < 0) {
									return;
								}
								paginate('backward');
							}}
						/>
						{contactedBusinessesFiltered.length > 0 ? (
							<span className={`${pageName}-count-text`}>
								{query.skip} -{' '}
								{query.skip + query.limit >
								contactedBusinessesCount
									? contactedBusinessesCount
									: query.skip + query.limit}
								<span className={`${pageName}-count-text-of`}>
									&nbsp;of&nbsp;{contactedBusinessesCount}
								</span>
							</span>
						) : (
							<span className={`${pageName}-count-text`}>
								0 - 0
								<span className={`${pageName}-count-text-of`}>
									&nbsp;of&nbsp; 0
								</span>
							</span>
						)}
						<ChevronColorless
							className={`${pageName}-page-arrow ${pageName}-page-arrow-right ${
								query.skip + query.limit >=
								contactedBusinessesCount
									? 'disabled-button'
									: ''
							}`}
							onClick={() => {
								if (
									query.skip + query.limit >=
									contactedBusinessesCount
								) {
									return;
								}
								paginate('forward');
							}}
						/>
					</div>
				</div>
			</section>

			{pageName === 'communication-center' && (
				<div className="flex items-center gap-4 py-4">
					<Button
						card
						variant="secondary"
						onClick={() => clearFilter()}
					>
						All
					</Button>
					<Button
						card
						variant="secondary"
						selected={query.leadYes}
						onClick={() => setNewQuery('leadYes', true)}
					>
						Leads
					</Button>
					<Button
						card
						variant="secondary"
						selected={query.repliedYes}
						onClick={() => {
							if (query.repliedYes) {
								const shouldResetContactMethod =
									!query.openCountOneFour &&
									!query.openCountFiveEight &&
									!query.openCountNinePlus &&
									!query.repliedNo;

								setNewQuery('repliedYes', false);

								if (shouldResetContactMethod) {
									setNewQuery('contactMethod', '');
								}
							} else {
								setNewQuery('repliedYes', true);
							}
						}}
					>
						Replied
					</Button>

					<Button
						card
						variant="secondary"
						selected={query.openCountOneFour}
						onClick={() => {
							if (query.openCountOneFour) {
								const shouldResetContactMethod =
									!query.repliedYes &&
									!query.repliedNo &&
									!query.openCountFiveEight &&
									!query.openCountNinePlus;

								setNewQuery('openCountOneFour', false);

								if (shouldResetContactMethod) {
									setNewQuery('contactMethod', '');
								}
							} else {
								setNewQuery('openCountOneFour', true);
							}
						}}
					>
						1 - 4 opens
					</Button>
				</div>
			)}
		</div>
	);
};

export default FilterComponent;
